import { useEffect, useRef, type ReactNode } from "react";

import { useBootstrap } from "../api/queries/useBootstrap";
import { AppShellDesktop } from "./AppShellDesktop";
import { AppShellPhone } from "./AppShellPhone";
import { AppShellTablet } from "./AppShellTablet";
import { BootstrapProblem } from "./BootstrapProblem";
import { DevToolsFooter } from "./DevToolsFooter";
import { useApiErrorWatch } from "./useApiErrorWatch";
import { usePlatform } from "./usePlatform";
import { WakingUpScreen } from "./WakingUpScreen";

function ShellSkeleton() {
  return (
    <div className="flex min-h-screen items-start gap-lg bg-background p-lg">
      <div className="h-64 w-48 animate-pulse rounded-md bg-muted" />
      <div className="h-8 flex-1 animate-pulse rounded-md bg-muted" />
    </div>
  );
}

/**
 * Chooses the shell by platform. Nothing platform-shaped is passed down; the page inside
 * is the `PlatformRoute` outlet.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const platform = usePlatform();
  const { isPending, isError, error, isSlow, refetch } = useBootstrap();
  const startedAt = useRef<number>(Date.now());

  useEffect(() => {
    if (isPending) startedAt.current = Date.now();
  }, [isPending]);

  if (isPending) {
    return isSlow ? (
      <WakingUpScreen startedAt={startedAt.current} onRetry={() => void refetch()} />
    ) : (
      <ShellSkeleton />
    );
  }

  const Shell =
    platform === "phone" ? AppShellPhone : platform === "tablet" ? AppShellTablet : AppShellDesktop;

  if (isError) {
    // A tenant-not-resolved failure is full page; any other failure is inline in the shell.
    return <BootstrapProblem error={error} onRetry={() => void refetch()} />;
  }

  return (
    <Shell>
      <ForbiddenBanner />
      {children}
      <DevToolsFooter />
    </Shell>
  );
}

/** The 403 in anger, inline for now — `ProblemAlert` is Phase 8. */
function ForbiddenBanner() {
  const { forbidden, dismiss } = useApiErrorWatch();
  if (!forbidden) return null;
  return (
    <div
      role="alert"
      className="m-md flex items-start gap-sm rounded-md border border-border bg-state-upload-invalid-bg p-sm text-state-upload-invalid-fg"
    >
      <p className="type-body flex-1">
        You don&apos;t have{" "}
        <span className="type-key">{forbidden.requiredPermission ?? "permission"}</span> here.
        {forbidden.detail ? ` ${forbidden.detail}` : ""}
      </p>
      <button type="button" className="type-caption underline" onClick={dismiss}>
        Dismiss
      </button>
    </div>
  );
}
